/**
 * @voltxampere/spice — Switch codec (S — voltage-controlled)
 *
 * Berkeley SPICE3 syntax: `S{name} N+ N- NC+ NC- MODEL_NAME`
 *
 * Examples:
 *   S1 in out ctl 0 SW_DEFAULT
 *
 * VoltXAmpere's schema models a *manual* switch (mechanical, user
 * toggles). The SPICE voltage-controlled switch carries control
 * terminals that don't map; we collapse to the manual switch and
 * record a warning so the user knows the threshold parameters were
 * dropped (Sprint 22+ may grow a controlled-switch component).
 *
 * Schema mapping: `type: "switch"`, params: `{ closed?, model }`.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ok, err, } from "./_base.js";
import { stringParam } from "./_param-helpers.js";
export const switchCodec = {
    spiceLetter: "S",
    schemaTypes: ["switch"],
    examples: ["S1 in out ctl 0 SW_DEFAULT"],
    parse(card, ctx) {
        // Accept 4-node controlled (most common) AND 2-node simple form.
        if (card.tokens.length < 3) {
            return err("S needs at least 2 nodes and a model name");
        }
        let nodes;
        let modelRaw;
        if (card.tokens.length >= 5) {
            // 4 nodes + model.
            const sliced = card.tokens.slice(0, 4);
            const lastModel = card.tokens[4];
            if (sliced.length !== 4 || lastModel === undefined) {
                return err("S missing 4 nodes + model");
            }
            nodes = sliced.slice(0, 2);
            modelRaw = lastModel;
            ctx.warnings.push(`${card.identifier}: voltage-controlled switch — control terminals ignored, mapped to manual switch`);
        }
        else if (card.tokens.length === 3) {
            // Compact: 2 nodes + model.
            const sliced = card.tokens.slice(0, 2);
            const m = card.tokens[2];
            if (sliced.length !== 2 || m === undefined) {
                return err("S missing nodes / model");
            }
            nodes = sliced;
            modelRaw = m;
        }
        else {
            return err("S has unexpected token count");
        }
        const params = {
            closed: false,
            model: modelRaw,
        };
        const component = {
            id: card.identifier,
            type: "switch",
            position: { x: 0, y: 0 },
            rotation: 0,
            flipped: false,
            label: card.identifier,
            params,
            terminals: [],
        };
        return ok(component, nodes);
    },
    emit(component, terminals) {
        const model = stringParam(component.params.model, "SW_DEFAULT");
        const [a, b] = terminals;
        // Emit as compact 2-node form. The "control" mapping is lost
        // by design (Sprint 22+); manual-switch round-trip stays
        // syntactically valid.
        return `${component.id} ${a ?? "0"} ${b ?? "0"} ${model}`;
    },
    owns(schemaType) {
        return schemaType === "switch";
    },
};
//# sourceMappingURL=switch.js.map